/**
 * Custom hook for enabling buyer roles
 * Handles enabling roles for a buyer with proper error handling and notifications
 */

import { useCallback, useState } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useBuyerEnableRolesMutationMutation } from '../../../generated';

export interface UseBuyerEnableRolesConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseBuyerEnableRolesResponse {
  enableRoles: (buyerId: string) => Promise<void>;
  isLoading: boolean;
}

export const useBuyerEnableRoles = ({
  onSuccess,
  onError,
}: UseBuyerEnableRolesConfig = {}): UseBuyerEnableRolesResponse => {
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useNotification();
  const [enableRolesMutation] = useBuyerEnableRolesMutationMutation();

  const enableRoles = useCallback(
    async (buyerId: string): Promise<void> => {
      setIsLoading(true);
      try {
        const result = await enableRolesMutation({
          variables: { buyerId },
        });

        if (result.data?.buyerDisableRoles?.__typename === 'Buyer') {
          // Success notification
          addNotification({
            title: 'Success',
            message: 'Roles & Permissions enabled successfully',
            type: 'success',
          });

          // Call success callback
          onSuccess?.();
        } else {
          const errorMessage =
            result.data?.buyerDisableRoles?.__typename === 'NotFoundError'
              ? 'Buyer not found'
              : 'Failed to enable roles';
          throw new Error(errorMessage);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An unexpected error occurred while enabling roles.';

        // Error notification
        addNotification({
          title: 'Failed to Enable Roles',
          message: errorMessage,
          type: 'error',
        });

        // Call error callback
        const error = err instanceof Error ? err : new Error(errorMessage);
        onError?.(error);

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [enableRolesMutation, addNotification, onSuccess, onError]
  );

  return {
    enableRoles,
    isLoading,
  };
};
